// Job costing API (COST-01..03, backend routes/jobCosting.ts). All money is dollars.
import { apiClient } from '@/lib/api'

export type CostType = 'LABOR' | 'MATERIAL' | 'SUBCONTRACTOR' | 'OTHER'

export interface CostTotals {
  estimatedHours: number
  actualHours: number
  estimatedCost: number
  actualCost: number
  varianceAmount?: number
  variancePercent: number | null
  estimatedProfit: number
  actualProfit: number
  estimatedMarginPercent: number | null
  marginPercent: number | null
}

export interface CostLine {
  id: string
  description: string
  costType: CostType
  quantity: number
  unit: string
  price: number
  estimatedHours: number | null
  actualHours: number
  estimatedCost: number
  actualCost: number
  variancePercent: number | null
}

export interface CostEntry {
  id: string
  purchaseReceiptId?: string | null
  kind: 'LABOR' | 'EXPENSE'
  costType: CostType
  hours: number | null
  rate: number | null
  amount: number
  vendor: string | null
  reimbursable: boolean
  receiptUrl: string | null
  description: string | null
  estimateLineId: string | null
  incurredAt: string
}

export interface JobCosting {
  jobId: string
  estimateTakenAt: string | null
  price: number
  lines: CostLine[]
  byCostType: Array<{ costType: CostType; estimatedCost: number; actualCost: number; variancePercent: number | null }>
  unassignedActualCost: number
  totals: CostTotals
  entries: CostEntry[]
  trade?: { id: string; name: string } | null
}

export interface LaborRateRow {
  id: string
  firstName: string
  lastName: string
  email: string
  laborCostRate: number | null
}

export type NewCostEntry =
  | { kind: 'LABOR'; userId?: string | null; hours: number; rate?: number; estimateLineId?: string | null; description?: string | null }
  | {
      kind: 'EXPENSE'
      costType?: CostType
      amount: number
      vendor?: string | null
      reimbursable?: boolean
      receiptUrl?: string | null
      estimateLineId?: string | null
      description?: string | null
    }

export const COST_TYPE_LABEL: Record<CostType, string> = {
  LABOR: 'Labour',
  MATERIAL: 'Materials',
  SUBCONTRACTOR: 'Subcontractor',
  OTHER: 'Other',
}

/** COST-01: margin colour thresholds (%) and per-trade green targets. */
export interface CostingSettings {
  redBelow: number
  greenAt: number
  tradeTargets: Record<string, number>
}
export const DEFAULT_COSTING_SETTINGS: CostingSettings = {
  redBelow: 26,
  greenAt: 33,
  tradeTargets: {
    remodel: 33, bath: 33, kitchen: 33, general: 33, painting: 40, roofing: 35, decks: 35,
    fence: 30, flooring: 30, siding: 30, 'tv mounting': 40,
  },
}

function asSettings(v: unknown): CostingSettings {
  const o = (v && typeof v === 'object' ? v : {}) as Partial<CostingSettings>
  return {
    redBelow: typeof o.redBelow === 'number' ? o.redBelow : DEFAULT_COSTING_SETTINGS.redBelow,
    greenAt: typeof o.greenAt === 'number' ? o.greenAt : DEFAULT_COSTING_SETTINGS.greenAt,
    tradeTargets: { ...DEFAULT_COSTING_SETTINGS.tradeTargets, ...(o.tradeTargets && typeof o.tradeTargets === 'object' ? o.tradeTargets : {}) },
  }
}

/** Green target for a trade: exact key, then the longest key the name contains. */
export function tradeTargetFor(s: CostingSettings, tradeName: string | null | undefined): number | null {
  if (!tradeName) return null
  const n = tradeName.trim().toLowerCase().replace(/\s+/g, ' ')
  if (s.tradeTargets[n] !== undefined) return s.tradeTargets[n]
  const hit = Object.keys(s.tradeTargets).filter((k) => n.includes(k)).sort((a, b) => b.length - a.length)[0]
  return hit ? s.tradeTargets[hit] : null
}

export interface JobMarginRow {
  jobId: string
  tradeName: string | null
  price: number
  marginPercent: number | null
  estimatedMarginPercent: number | null
}
export interface TradeMarginRow {
  tradeId: string | null
  tradeName: string
  jobCount: number
  price: number
  estimatedCost: number
  actualCost: number
  estimatedMarginPercent: number | null
  marginPercent: number | null
  targetMarginPercent: number
}

// The design vault uses disposable in-memory data, never the tenant API.
export function designMode(): boolean {
  if (typeof window === 'undefined') return false
  const design = new URLSearchParams(window.location.search).get('__design')
  return design === 'job-costing'
}
const fixtureJobId = '__design-job-costing'
function guardJob(jobId: string) {
  if ((jobId === fixtureJobId) !== designMode()) throw new Error('Demo jobs and live jobs must be opened separately.')
}
let fixtureSettings: CostingSettings = structuredClone(DEFAULT_COSTING_SETTINGS)
let fixturePeople: LaborRateRow[] = [
  { id: 'design-person', firstName: '[DEMO] Alex', lastName: 'Builder', email: 'alex@example.test', laborCostRate: 45 },
]
const fixtureJob: JobCosting = {
  jobId: fixtureJobId, estimateTakenAt: '2026-01-01T00:00:00Z', price: 4800,
  trade: { id: 'design-trade', name: 'Painting' },
  lines: [
    { id: 'design-labor', description: '[DEMO] Labour', costType: 'LABOR', quantity: 40, unit: 'hour', price: 45, estimatedHours: 40, actualHours: 0, estimatedCost: 1800, actualCost: 0, variancePercent: -100 },
    { id: 'design-material', description: '[DEMO] Materials', costType: 'MATERIAL', quantity: 1, unit: 'each', price: 800, estimatedHours: null, actualHours: 0, estimatedCost: 800, actualCost: 0, variancePercent: -100 },
  ],
  byCostType: [], unassignedActualCost: 0, entries: [],
  totals: { estimatedHours: 40, actualHours: 0, estimatedCost: 2600, actualCost: 0, variancePercent: -100, estimatedProfit: 2200, actualProfit: 4800, estimatedMarginPercent: 45.83, marginPercent: 100 },
}
function fixtureResult(): JobCosting {
  const data = structuredClone(fixtureJob)
  for (const line of data.lines) {
    const entries = data.entries.filter((entry) => entry.estimateLineId === line.id)
    line.actualCost = entries.reduce((sum, entry) => sum + entry.amount, 0)
    line.actualHours = entries.reduce((sum, entry) => sum + (entry.hours ?? 0), 0)
    line.variancePercent = line.estimatedCost ? Math.round((line.actualCost - line.estimatedCost) / line.estimatedCost * 100) : null
  }
  data.unassignedActualCost = data.entries.filter((entry) => !entry.estimateLineId).reduce((sum, entry) => sum + entry.amount, 0)
  data.totals.actualCost = data.entries.reduce((sum, entry) => sum + entry.amount, 0)
  data.totals.actualHours = data.entries.reduce((sum, entry) => sum + (entry.hours ?? 0), 0)
  data.totals.actualProfit = data.price - data.totals.actualCost
  data.totals.marginPercent = (data.totals.actualProfit / data.price) * 100
  data.totals.variancePercent = Math.round((data.totals.actualCost - data.totals.estimatedCost) / data.totals.estimatedCost * 100)
  data.byCostType = (Object.keys(COST_TYPE_LABEL) as CostType[]).map((costType) => ({ costType, estimatedCost: data.lines.filter((line) => line.costType === costType).reduce((sum, line) => sum + line.estimatedCost, 0), actualCost: data.entries.filter((entry) => entry.costType === costType).reduce((sum, entry) => sum + entry.amount, 0), variancePercent: null }))
  return data
}

export const costingApi = {
  /** COST-03: upload a receipt photo/PDF (same storage as job photos); returns its URL. */
  uploadReceipt: async (jobId: string, file: File) => {
    if (jobId === fixtureJobId || designMode()) throw new Error('Receipt uploads are unavailable in demo. Nothing was uploaded.')
    const fd = new FormData()
    fd.append('receipt', file)
    return apiClient
      .post(`/api/job-costing/jobs/${jobId}/receipts`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => String(r.data?.data?.receiptUrl ?? ''))
  },
  jobMargins: () => designMode()
    ? Promise.resolve([] as JobMarginRow[])
    : apiClient.get('/api/job-costing/jobs').then((r) => (Array.isArray(r.data?.data) ? r.data.data : []) as JobMarginRow[]),
  byTrade: (range?: { from?: string; to?: string }) => designMode()
    ? Promise.resolve([] as TradeMarginRow[])
    : apiClient.get('/api/job-costing/by-trade', { params: range }).then((r) => (Array.isArray(r.data?.data) ? r.data.data : []) as TradeMarginRow[]),
  settings: () => designMode() ? Promise.resolve(structuredClone(fixtureSettings)) : apiClient.get('/api/job-costing/settings').then((r) => asSettings(r.data?.data)),
  updateSettings: (body: Partial<CostingSettings>) => {
    if (designMode()) { fixtureSettings = { ...fixtureSettings, ...body }; return Promise.resolve(structuredClone(fixtureSettings)) }
    return apiClient.put('/api/job-costing/settings', body).then((r) => asSettings(r.data?.data))
  },
  job: async (jobId: string) => { guardJob(jobId); return designMode() ? fixtureResult() : apiClient.get(`/api/job-costing/jobs/${jobId}`).then((r) => r.data?.data as JobCosting) },
  snapshot: async (jobId: string) => { guardJob(jobId); return designMode() ? undefined : apiClient.post(`/api/job-costing/jobs/${jobId}/snapshot`, {}) },
  addEntry: async (jobId: string, body: NewCostEntry) => {
    guardJob(jobId)
    if (designMode() && jobId === fixtureJobId) {
      const rate = body.kind === 'LABOR' ? (body.rate ?? fixturePeople.find((p) => p.id === body.userId)?.laborCostRate ?? 0) : null
      fixtureJob.entries.push({ id: `design-entry-${Date.now()}-${fixtureJob.entries.length}`, kind: body.kind, costType: body.kind === 'LABOR' ? 'LABOR' : (body.costType ?? 'OTHER'), hours: body.kind === 'LABOR' ? body.hours : null, rate, amount: body.kind === 'LABOR' ? body.hours * (rate ?? 0) : body.amount, vendor: body.kind === 'EXPENSE' ? (body.vendor ?? null) : null, reimbursable: body.kind === 'EXPENSE' ? (body.reimbursable ?? false) : false, receiptUrl: body.kind === 'EXPENSE' ? (body.receiptUrl ?? null) : null, description: body.description ?? null, estimateLineId: body.estimateLineId ?? null, incurredAt: new Date().toISOString() })
      return Promise.resolve()
    }
    return apiClient.post(`/api/job-costing/jobs/${jobId}/entries`, body)
  },
  deleteEntry: async (jobId: string, entryId: string) => {
    guardJob(jobId)
    if (designMode() && jobId === fixtureJobId) { fixtureJob.entries = fixtureJob.entries.filter((entry) => entry.id !== entryId); return Promise.resolve() }
    return apiClient.delete(`/api/job-costing/jobs/${jobId}/entries/${entryId}`)
  },
  laborRates: () => designMode() ? Promise.resolve(structuredClone(fixturePeople)) : apiClient.get('/api/job-costing/labor-rates').then((r) => (Array.isArray(r.data?.data) ? r.data.data : []) as LaborRateRow[]),
  setLaborRate: async (userId: string, laborCostRate: number | null) => {
    if ((userId === 'design-person') !== designMode()) throw new Error('Demo rates and live rates must be edited separately.')
    if (designMode()) { fixturePeople = fixturePeople.map((p) => p.id === userId ? { ...p, laborCostRate } : p); return Promise.resolve() }
    return apiClient.put(`/api/job-costing/labor-rates/${userId}`, { laborCostRate })
  },
}
